import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomerDetailView from './customerDetailView';

import MatterCardListing from './matterCardListing';
import { CustomerSections, ICustomerType } from '../../constants/customer';
import { useLocation, useParams } from 'react-router-dom';
import { useFetchCustomerById } from '../../hooks/useFetchCustomerById';
import { ICustomer } from '../../interface/customer';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import MatterRequestListing from '../conflictSearch/matterRequestListing';
import ConflictCheckListing from '../conflictSearch/conflictCheckListing';
import Notes from '../conflictSearch/notes';
import { NotesEntity } from '../../constants/notes';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useUser } from '../../context/UserContext';
import { UserType } from '../../constants/constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomTabs from '../customTabs/customTabs';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';

const CustomerDetailLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { goBack } = useNavigationStack();
  const { setTitle } = usePage();
  const { id: customerId } = useParams();
  const { trackPage } = useAnalytics();
  const { user } = useUser();
  const fromTab = location?.state?.fromTab ?? CustomerSections.general;
  const [selectedTab, setSelectedTab] = useState<string>(fromTab.toString());

  const {
    data: customerDetails,
    isLoading: isCustomerDetailsLoading,
    refetch,
  } = useFetchCustomerById({
    id: customerId,
    withFavoriteInformation: true,
    relations: [
      'responsibleUser',
      'parentCompany:customerType,name',
      'primaryAddress',
      'privatePersonCompany:name',
    ],
    fields: [
      'name',
      'homeCountry',
      'primaryPhone',
      'type',
      'personalId',
      'vatNumber',
      'email',
      'businessId',
      'hasImportantNotes',
      'hasNotes',
      'companyNumber',
      'privatePersonNumber',
    ],
  });

  useEffect(() => {
    trackPage(AnalyticsPage.CUSTOMER_DETAIL);
  }, [trackPage]);

  const handleBack = () => {
    goBack();
  };
  const customerNumber =
    customerDetails?.companyNumber ?? customerDetails?.privatePersonNumber ?? null;
  useEffect(() => {
    if (customerDetails && !isCustomerDetailsLoading) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={handleBack}
          title={
            customerNumber ? `${customerNumber} - ${customerDetails?.name}` : customerDetails?.name
          }
          subTitle={t('features.customers')}
        />,
      );
    }
  }, [customerDetails, isCustomerDetailsLoading]);

  const items = useMemo(
    () => [
      {
        key: CustomerSections.general.toString(),
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <CustomerDetailView
              data-testid="customer-detail-view"
              details={customerDetails as ICustomer}
              refetch={refetch}
              isLoading={isCustomerDetailsLoading}
            />
          </DetailScrollArea>
        ),
      },
      {
        key: CustomerSections.matters.toString(),
        label: t('common:tab.matters'),
        children: (
          <MatterCardListing data-testid="matter-card-listing" type={customerDetails?.type} />
        ),
      },
      {
        key: CustomerSections.matterRequests.toString(),
        label: t('common:tab.matterRequests'),
        children: (
          <MatterRequestListing
            data-testid="matter-request-listing"
            type={customerDetails?.type ?? ''}
            isFromCustomer
            customer={customerDetails}
          />
        ),
      },
      ...(user?.userType === UserType.RESTRICTED
        ? []
        : [
            {
              key: CustomerSections.conflictChecks.toString(),
              label: t('common:tab.conflictChecks'),
              children: (
                <ConflictCheckListing
                  data-testid="conflict-check-listing"
                  type={customerDetails?.type ?? ''}
                />
              ),
            },
          ]),
      {
        key: CustomerSections.notes.toString(),
        label: t('common:tab.notes'),
        children: (
          <Notes
            data-testid="customer-notes"
            entitySubject={customerDetails?.name ?? ''}
            entity={
              customerDetails?.type === ICustomerType.COMPANY
                ? NotesEntity.COMPANY
                : NotesEntity.PRIVATEPERSON
            }
          />
        ),
      },
    ],
    [t, customerDetails, isCustomerDetailsLoading],
  );

  return (
    <CustomTabs
      data-testid="customer-detail-tabs"
      variant="pill"
      activeKey={selectedTab}
      onChange={setSelectedTab}
      items={items}
    />
  );
};

export default CustomerDetailLayout;
